/**
 * My profile program hook - Elm-ish state management.
 *
 * This hook follows the Elm architecture pattern:
 * - Model: Immutable state
 * - Update: Pure function to transition state
 * - Effects: Side effects handled separately
 *
 * S018 authority: Figma node 39:4695
 *
 * Security: All API calls are made here, not in the view.
 *
 * C1: Instance-owned scheduler - no module-level mutable state.
 * Each hook instance has its own controller and mounted ref.
 * Two simultaneous programs do not interact.
 *
 * C2: One generation-keyed Effect.
 * Strict Mode setup/cleanup/setup leaves exactly one live authoritative request.
 */
import { useReducer, useCallback, useEffect, useRef } from 'react';
import { updateMyProfile } from './update';
import { createValidatedProfileSummary } from './model';
import type { MyProfileModel } from './model';
import type { MyProfileMsg } from './messages';
import type { MyProfilePort } from './port';
import { useAuthStore } from '@/store/authStore';

/**
 * Result of using the my profile program.
 */
export interface UseMyProfileProgramResult {
  /** Current model state */
  model: MyProfileModel;
  /** Retry loading the profile */
  retry: () => void;
  /** Request logout */
  logout: () => void;
  /** Clear logout error */
  clearLogoutError: () => void;
}

/**
 * Run profile load effect.
 * Hook owns the AbortController and cleanup.
 * Returns message to dispatch or null if aborted.
 *
 * P0 Fix: Aborted effects return null, never synthetic state transitions.
 * This prevents obsolete work from mutating the model after Strict Mode cleanup.
 */
async function runLoadMyProfile(
  port: MyProfilePort,
  loadId: number,
  signal: AbortSignal
): Promise<MyProfileMsg | null> {
  try {
    const rawProfile = await port.loadMyProfile(signal);

    // Ignore result if aborted (Strict Mode cleanup case)
    if (signal.aborted) {
      return null;
    }

    // Validate profile data before entering ready state
    const profile = createValidatedProfileSummary(rawProfile);
    if (!profile) {
      // Fail closed - invalid profile data
      return { type: 'LoadFailed', loadId };
    }

    return {
      type: 'LoadSucceeded',
      loadId,
      profile,
    };
  } catch {
    // Ignore error if aborted (Strict Mode cleanup case)
    if (signal.aborted) {
      return null;
    }

    // Non-abort error - dispatch failure
    return { type: 'LoadFailed', loadId };
  }
}

/**
 * Run logout effect through production auth boundary.
 * Returns message to dispatch rather than dispatching directly.
 */
async function runLogout(requestId: number): Promise<MyProfileMsg> {
  try {
    // Use existing production auth store logout
    const { logout } = useAuthStore.getState();
    await logout();

    return {
      type: 'LogoutSucceeded',
      requestId,
    };
  } catch {
    return {
      type: 'LogoutFailed',
      requestId,
    };
  }
}

/**
 * Initial model state for useReducer.
 * Initial loadId is 1 to match production.
 */
function getInitialModel(): MyProfileModel {
  return {
    status: 'loading',
    activeLoadId: 1,
  };
}

/**
 * Hook for my profile state management.
 * Returns model and dispatch-like methods.
 *
 * C2: One generation-keyed Effect.
 * init() -> loading generation 1 -> effect LoadMyProfile(1)
 * Strict Mode cleanup/setup leaves exactly one live authoritative request.
 */
export function useMyProfileProgram(port: MyProfilePort): UseMyProfileProgramResult {
  const [model, rawDispatch] = useReducer(updateMyProfile, undefined, getInitialModel);

  // Stable dispatch wrapper
  const dispatch = useCallback((msg: MyProfileMsg) => {
    rawDispatch(msg);
  }, [rawDispatch]);

  // Instance-owned state - C1
  const portRef = useRef(port);
  portRef.current = port;

  // Track mounted state for this instance - C3: cleanup on unmount
  const isMountedRef = useRef(true);

  // Track next request ID for logout
  const nextRequestIdRef = useRef(1);

  // C3: Cleanup on unmount - synchronous and owned
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // C2: One generation-keyed Effect for profile loading
  // Strict Mode setup/cleanup/setup leaves exactly one live authoritative request
  useEffect(() => {
    // Only run when loading
    if (model.status !== 'loading') {
      return;
    }

    const controller = new AbortController();
    let ignored = false; // P0: Local guard against stale effect completions
    const loadId = model.activeLoadId;

    // Dispatch LoadStarted to trigger effect re-run on subsequent generations
    // This is idempotent - returns same state if already in this generation
    dispatch({ type: 'LoadStarted', loadId });

    // Run the effect and dispatch result if not ignored and still mounted
    void runLoadMyProfile(portRef.current, loadId, controller.signal).then((msg) => {
      // P0: Check both local ignored flag and instance mounted ref
      if (!ignored && isMountedRef.current && msg !== null) {
        dispatch(msg);
      }
    });

    // Cleanup aborts only this effect's controller
    // Also sets local ignored flag to prevent stale dispatches
    return () => {
      ignored = true;
      controller.abort();
    };
  }, [model.status, model.activeLoadId, dispatch]);

  // Handle retry - creates new generation
  const retry = useCallback(() => {
    // Dispatch RetryRequested which increments activeLoadId
    dispatch({ type: 'RetryRequested' });
  }, [dispatch]);

  // Derive logout request ID for effect dependency
  const logoutRequestId: number | null =
    model.status === 'ready' && model.logout.status === 'submitting'
      ? model.logout.requestId
      : null;

  // Effect to handle logout execution
  // Depends on logoutRequestId to ensure it runs when submit starts
  useEffect(() => {
    if (logoutRequestId === null) {
      return;
    }

    let cancelled = false;

    void runLogout(logoutRequestId).then((msg) => {
      if (!cancelled && isMountedRef.current) {
        dispatch(msg);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [logoutRequestId, dispatch]);

  // Logout
  const logout = useCallback(() => {
    const requestId = nextRequestIdRef.current++;
    dispatch({ type: 'LogoutRequested', requestId });
  }, [dispatch]);

  // Clear logout error
  const clearLogoutError = useCallback(() => {
    dispatch({ type: 'LogoutErrorCleared' });
  }, [dispatch]);

  return {
    model,
    retry,
    logout,
    clearLogoutError,
  };
}
